import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';

import { Box, Button, Skeleton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { useTransl } from 'shared/lib/i18n';

import DeliverySignOutContainer from '../FrontDesk/LookUp/DeliverySignOutContainer';
import CreateDeliveryContainer from './CreateDeliveryContainer';

import ModalHandlerComponent from '../Modal/ModalHandlerComponent';
import DeliverySearchComponent from './DeliverySearchComponent';

import { ADD_DELIVERY, DELIVERY_SIGN_OUT } from './constants';

import ListActions from '../Table/ListActions';

const StyledHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
  fontSize: 48,
  flex: 1,
  marginLeft: '14rem',
  textTransform: 'uppercase',
  color: 'primary',
  textAlign: 'center',
  [theme.breakpoints.down('md')]: {
    fontSize: 40,
    margin: 'auto',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 32,
    margin: 'auto',
  },
}));

const StyledAction = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    marginTop: '1rem',
    alignSelf: 'flex-end',
  },
}));
const StyledUnitsContainer = styled(Box)(({ theme }) => ({
  margin: '4rem 0',
  display: 'grid',
  gridTemplateColumns: 'repeat(8, 1fr)',
  gap: '1.6rem 1rem',
  [theme.breakpoints.down('xl')]: {
    gridTemplateColumns: 'repeat(6, 1fr)',
  },
  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: 'repeat(5, 1fr)',
  },
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'repeat(4, 1fr)',
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
}));

const StyledUnitCard = styled(Button)(() => ({
  height: '10rem',
  backgroundColor: '#F7F7F7',
  '&:active': {
    backgroundColor: '#F7F7F7',
  },
}));

const DeliverySelfSingOutComponent = (props) => {
  const { t } = useTransl();
  const {
    isValidQr,
    units,
    modalData,
    setModalData,
    addDeliveryModalProps,
    buildingId,
    search,
    setSearch,
    getDeliverySignOutModalProps,
  } = props;

  const addDelivery = [
    {
      label: t('delivery.add'),
      onClick: () => setModalData(addDeliveryModalProps),
    },
  ];

  if (!isValidQr) {
    return (
      <Box display="flex" height="100vh" className="content-area">
        <Typography variant="h5" textAlign="center" className="center-content">
          {t('delivery.qrInvalid')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box display="flex" className="content-area">
      <StyledHeader>
        <StyledTitle>{t('delivery.selfSignOut')}</StyledTitle>
        <StyledAction>
          <ListActions actions={addDelivery} />
        </StyledAction>
      </StyledHeader>
      <Box pt="2.5rem" lineHeight="1.5rem">
        <DeliverySearchComponent
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </Box>
      <StyledUnitsContainer>
        {isEmpty(units) && !search
          ? Array.from({ length: 50 })
              .map((v, i) => i)
              .map((key) => (
                <Box
                  key={key}
                  sx={{
                    height: '10rem',
                    borderRadius: '1rem',
                    overflow: 'hidden',
                  }}
                >
                  <Skeleton variant="rectangular" width="100%" height="100%" />
                </Box>
              ))
          : Object.values(units)?.map(({ name, id }) => (
              <StyledUnitCard
                key={id}
                onClick={() =>
                  setModalData(getDeliverySignOutModalProps(id, name))
                }
              >
                <Typography fontWeight="bold" variant="h5">
                  {name}
                </Typography>
              </StyledUnitCard>
            ))}
      </StyledUnitsContainer>
      {Boolean(search) && isEmpty(units) && (
        <Typography textAlign="center" variant="h5">
          {t('unit.noUnits')}
        </Typography>
      )}
      {modalData.modalName === ADD_DELIVERY && (
        <ModalHandlerComponent {...modalData}>
          <CreateDeliveryContainer
            onModalClose={modalData.onModalClose}
            frontDeskBuildingId={buildingId}
          />
        </ModalHandlerComponent>
      )}
      {modalData.modalName === DELIVERY_SIGN_OUT && (
        <ModalHandlerComponent {...modalData}>
          <DeliverySignOutContainer
            onModalClose={modalData.onModalClose}
            unit={modalData.unit}
            isSelfSignOut
          />
        </ModalHandlerComponent>
      )}
    </Box>
  );
};

DeliverySelfSingOutComponent.propTypes = {
  isValidQr: PropTypes.bool.isRequired,
  units: PropTypes.instanceOf(Object).isRequired,
  location: PropTypes.instanceOf(Object).isRequired,
  modalData: PropTypes.instanceOf(Object).isRequired,
  addDeliveryModalProps: PropTypes.instanceOf(Object).isRequired,
  setModalData: PropTypes.func.isRequired,
  setSearch: PropTypes.func.isRequired,
  getDeliverySignOutModalProps: PropTypes.func.isRequired,
  buildingId: PropTypes.string.isRequired,
  search: PropTypes.string,
};

export default DeliverySelfSingOutComponent;
